import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  Link,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { clearCachedState, setCacheUser } from "@/hooks/use-cached-state";
import { resetUserRoleCache } from "@/hooks/use-user-role";
import { resetUnreadChatsCache } from "@/hooks/use-unread-chats";

import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-primary">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Halaman tidak ditemukan</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Halaman yang Anda cari tidak tersedia.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Kembali
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">Terjadi kesalahan</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <button
          onClick={() => { router.invalidate(); reset(); }}
          className="mt-6 inline-flex items-center justify-center rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Coba lagi
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0f766e" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-title", content: "Al-Munajah" },
      { title: "MI Al-Munajah Admin" },
      { name: "description", content: "Panel admin MI Al-Munajah — kelola siswa, guru, pengumuman, jadwal, absensi, dan galeri." },
      { property: "og:title", content: "MI Al-Munajah Admin" },
      { name: "twitter:title", content: "MI Al-Munajah Admin" },
      { property: "og:description", content: "Panel admin MI Al-Munajah — kelola siswa, guru, pengumuman, jadwal, absensi, dan galeri." },
      { name: "twitter:description", content: "Panel admin MI Al-Munajah — kelola siswa, guru, pengumuman, jadwal, absensi, dan galeri." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/365d8c3f-f925-480a-9e3e-738a7619a603/id-preview-da7bb247--a4d43d27-a8ea-4296-a7bc-5bf0fe2d3f4c.lovable.app-1780545115859.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/365d8c3f-f925-480a-9e3e-738a7619a603/id-preview-da7bb247--a4d43d27-a8ea-4296-a7bc-5bf0fe2d3f4c.lovable.app-1780545115859.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", href: "/__l5e/assets-v1/9c1d96dc-bf2f-4ece-9fb7-6dcff131aec3/logo-mi-almunajah.png" },
      { rel: "icon", type: "image/png", href: "/__l5e/assets-v1/9c1d96dc-bf2f-4ece-9fb7-6dcff131aec3/logo-mi-almunajah.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="id">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

const RQ_PERSIST_KEY = "almunajah-rq-cache";
const ACCOUNT_CACHE_PREFIXES = [
  "almunajah-cache:",
  "almunajah-role-cache:",
  "almunajah-unread-cache:",
  "notif:lastSeen:",
];
const ACCOUNT_CACHE_KEYS = new Set<string>([
  RQ_PERSIST_KEY,
  "fcm_token:synced",
]);

// Keys yang HARUS dipertahankan lintas logout (device-level, bukan akun-level).
const PRESERVE_KEYS = new Set<string>([
  "theme",
  "vite-ui-theme",
  "almunajah-device-id",
  "fcm-token",
  "fcm_token",
  "fcm_token:status",
  "fcm_token:registration_requested",
  "passkey_device_registered",
  "passkey_last_email",
]);

function purgeAccountCaches() {
  if (typeof window === "undefined") return;
  try {
    Object.keys(window.localStorage)
      .filter((key) => ACCOUNT_CACHE_KEYS.has(key) || ACCOUNT_CACHE_PREFIXES.some((prefix) => key.startsWith(prefix)))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch { /* ignore */ }
}

/**
 * Hancurkan SEMUA cache saat logout: localStorage, sessionStorage,
 * IndexedDB, dan Cache Storage. Hanya sisakan key device-level yang
 * ada di PRESERVE_KEYS. Dipanggil hanya pada event SIGNED_OUT.
 */
async function nukeAllBrowserCaches() {
  if (typeof window === "undefined") return;
  try {
    const ls = window.localStorage;
    Object.keys(ls).forEach((key) => {
      if (!PRESERVE_KEYS.has(key)) ls.removeItem(key);
    });
  } catch { /* ignore */ }
  try {
    window.sessionStorage.clear();
  } catch { /* ignore */ }
  try {
    if ("indexedDB" in window && typeof indexedDB.databases === "function") {
      const dbs = await indexedDB.databases();
      await Promise.all(
        (dbs ?? []).map((db) => {
          if (!db?.name) return Promise.resolve();
          return new Promise<void>((resolve) => {
            const req = indexedDB.deleteDatabase(db.name!);
            req.onsuccess = req.onerror = req.onblocked = () => resolve();
          });
        }),
      );
    }
  } catch { /* ignore */ }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* ignore */ }
}

function resetAccountScopedState() {
  clearCachedState();
  resetUserRoleCache();
  resetUnreadChatsCache();
  purgeAccountCaches();
}

function AuthSync() {
  const router = useRouter();
  const queryClient = useQueryClient();
  useEffect(() => {
    let lastUserId: string | null = null;
    let bootstrapped = false;

    // Set namespace cache awal berdasarkan sesi yang sudah ada, agar user yang
    // sudah login melihat tampilan terakhirnya sebelum fetch pertama selesai.
    supabase.auth.getSession().then(({ data }) => {
      lastUserId = data.session?.user?.id ?? null;
      setCacheUser(lastUserId);
      bootstrapped = true;
    });

    const hardResetAccountCaches = () => {
      queryClient.cancelQueries();
      queryClient.clear();
      resetAccountScopedState();
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "INITIAL_SESSION" && event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      const newUserId = session?.user?.id ?? null;
      const userChanged = newUserId !== lastUserId;

      if (event === "INITIAL_SESSION") {
        lastUserId = newUserId;
        setCacheUser(newUserId);
        bootstrapped = true;
        return;
      }

      // SIGNED_IN pertama saat app baru dimuat (session sudah ada di storage)
      // — jangan invalidate apapun, biarkan cache tampil mulus tanpa flash.
      if (event === "SIGNED_IN" && bootstrapped === false) {
        lastUserId = newUserId;
        setCacheUser(newUserId);
        bootstrapped = true;
        return;
      }

      if (event === "SIGNED_OUT") {
        // Logout = hancurkan SEMUA cache di browser (in-memory + persisten +
        // IndexedDB + Cache Storage). Tidak ada jejak akun sebelumnya.
        hardResetAccountCaches();
        void nukeAllBrowserCaches();
        lastUserId = newUserId;
        setCacheUser(newUserId);
        router.invalidate();
        return;
      }

      if (userChanged) {
        // Ganti akun tanpa logout eksplisit — buang state akun lama.
        hardResetAccountCaches();
        lastUserId = newUserId;
        setCacheUser(newUserId);
        router.invalidate();
        return;
      }

      // Same user (token refresh / user update biasa) — JANGAN invalidate
      // apapun supaya tampilan tetap mulus tanpa flash / skeleton berulang.
      lastUserId = newUserId;
    });


    const refreshOnReconnect = () => {
      if (!navigator.onLine) return;
      router.invalidate();
      queryClient.invalidateQueries();
    };

    window.addEventListener("online", refreshOnReconnect);
    window.addEventListener("almunajah:account-reset", hardResetAccountCaches);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("online", refreshOnReconnect);
      window.removeEventListener("almunajah:account-reset", hardResetAccountCaches);
    };
  }, [router, queryClient]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthSync />
      <Outlet />
      <Toaster position="top-right" richColors />
    </QueryClientProvider>
  );
}
